import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'config/axios';
import PerformanceHistory from './PerformanceHistory';
import BCAPerformanceHistory from './BCAPerformanceHistory';
import AssignmentTracking from './AssignmentTracking';
import FocusAreas from './FocusAreas';

import './index.css';

const TEST_TYPE = { 'Digital SAT': 'SAT', 'Digital PSAT': 'PSAT', 'Digital ACT': 'ACT', 'Digital SHSAT': 'SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA' };
const TYPE_ORDER = ['SAT', 'PSAT', 'ACT', 'SHSAT', 'TSIA2', 'BCA'];
// BCA questions have no categories, so it has no Focus Areas tab.
const CATEGORY_SUB_TABS = ['focus'];

type OverviewProps = {
    lastTestTestType: string;
    SAT: any[];
    PSAT: any[];
    ACT: any[];
    SHSAT: any[];
    TSIA2?: any[];
    BCA?: any[];
    students?: any[];
    tests?: any[];
    assignments?: any[];
    vocab?: any[];
};

const SUB_TABS = [
    {
        id: 'performance',
        label: 'Performance History',
        icon: <path d="M3 3v18h18M7 14l4-4 4 4 5-5" />,
    },
    {
        id: 'assignments',
        label: 'Assignment Tracking',
        icon: <><path d="M9 11l3 3 8-8" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
    },
    {
        id: 'focus',
        label: 'Focus Areas',
        icon: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    },
];

function Overview({ lastTestTestType, SAT, PSAT, ACT, SHSAT, TSIA2 = [], BCA = [], students = [], tests = [], assignments = [] }: OverviewProps) {
    const { classId } = useParams();
    const [vocab, setVocab] = useState<any[]>([]);

    // Vocab-set completion lives behind its own endpoint; fetch it for the
    // Assignment Tracking sub-tab. Fails silently (e.g. orgs with vocab hidden).
    useEffect(() => {
        let active = true;
        axios.get(`vocab/tutor/classes/${classId}/vocab-sets/`)
            .then(res => {
                if (!active) return;
                const rows = (res.data.assignedSets || []).map((vs: any) => ({
                    id: vs.id,
                    name: vs.name,
                    words: vs.wordCount,
                    assignDate: vs.assignDate,
                    numAssigned: vs.assignedCount,
                    numTaken: vs.completedCount,
                    assignedStudentIds: vs.assignedStudentIds,
                    completedStudentIds: vs.completedStudentIds,
                }));
                setVocab(rows);
            })
            .catch(() => { if (active) setVocab([]); });
        return () => { active = false; };
    }, [classId]);

    const dataMap = useMemo(() => ({ SAT, PSAT, ACT, SHSAT, TSIA2, BCA }), [SAT, PSAT, ACT, SHSAT, TSIA2, BCA]);

    const availableTypes = useMemo(
        () => TYPE_ORDER.filter(t => dataMap[t] && dataMap[t].length),
        [dataMap]
    );

    // Default to SAT on open/refresh; only fall back to another type if this class
    // has no SAT data (handled by the effect below once data is available).
    const [testType, setTestType] = useState<string>('SAT');
    const [subTab, setSubTab] = useState('performance');
    const isBCA = testType === 'BCA';
    const subTabs = isBCA ? SUB_TABS.filter(t => !CATEGORY_SUB_TABS.includes(t.id)) : SUB_TABS;

    // Leaving a hidden sub-tab selected after switching to BCA would show nothing.
    useEffect(() => {
        if (!subTabs.some(t => t.id === subTab)) setSubTab('performance');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testType]);

    useEffect(() => {
        if (availableTypes.length && !availableTypes.includes(testType)) {
            setTestType(availableTypes.includes('SAT') ? 'SAT' : availableTypes[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableTypes]);

    const activeData = dataMap[testType] || [];
    // Focus Areas needs the most recent test that actually has graded results.
    // activeData is newest-first; a freshly assigned-but-untaken test has an
    // empty studentDomainBreakdown, so skip past those to the latest with data.
    const latestTest = useMemo(() => {
        const withData = activeData.find(
            (t: any) => Array.isArray(t?.studentDomainBreakdown) && t.studentDomainBreakdown.length > 0
        );
        return withData || activeData[0];
    }, [activeData]);

    return (
        <div className="cdr">
            <div className="cdr__page-head">
                <div className="cdr__page-title">Class Data</div>
                {availableTypes.length > 0 && (
                    <div className="cdr__subjects">
                        {availableTypes.map(t => (
                            <button
                                key={t}
                                className={testType === t ? 'active' : ''}
                                onClick={() => setTestType(t)}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="cdr__tabs" role="tablist">
                {subTabs.map(t => (
                    <button
                        key={t.id}
                        className={subTab === t.id ? 'active' : ''}
                        onClick={() => setSubTab(t.id)}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            {t.icon}
                        </svg>
                        {t.label}
                    </button>
                ))}
            </div>

            {subTab === 'performance' && (isBCA
                ?   <BCAPerformanceHistory tests={BCA} />
                :   <PerformanceHistory testType={testType} SAT={SAT} PSAT={PSAT} ACT={ACT} SHSAT={SHSAT} TSIA2={TSIA2} />
            )}
            {subTab === 'assignments' && (
                <AssignmentTracking
                    testType={testType}
                    students={students}
                    tests={tests}
                    assignments={assignments}
                    vocab={vocab}
                />
            )}
            {subTab === 'focus' && !isBCA && <FocusAreas latestTest={latestTest} />}
        </div>
    );
}

export default Overview;
